import { Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateField } from '@/components/ui/date-field'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Select } from '@/components/ui/select'
import { toast } from 'sonner'
import { useCreatePayment } from '../../api'
import { usePersonelList } from '../../hooks/usePersonelStore'
import { todayIso } from '../../lib/fileExport'
import { donemFromDate, periodKey } from '../../lib/odemeActions'
import type { OdemeTur } from '../../types'
import { Field, ModalFooter } from '../actions/Field'
import { apiErrorMessage } from '@/i18n'

const TURLER: OdemeTur[] = ['maas', 'avans', 'masraf']

export function OdemePlanModal({ defaultTur, onClose }: { defaultTur: OdemeTur; onClose: () => void }) {
  const { t } = useTranslation()
  const list = usePersonelList()
  const create = useCreatePayment()
  const [personelId, setPersonelId] = useState('')
  const [tur, setTur] = useState<OdemeTur>(defaultTur)
  const [tutar, setTutar] = useState('')
  const [tarih, setTarih] = useState(todayIso())
  const [submitted, setSubmitted] = useState(false)

  const required = t('hr.personel.actions.required')
  const errors = {
    personel: personelId ? undefined : required,
    tutar: Number(tutar) > 0 ? undefined : required,
    tarih: tarih ? undefined : required,
  }
  const show = (e?: string) => (submitted ? e : undefined)

  const submit = () => {
    setSubmitted(true)
    const p = list.find((x) => x.id === personelId)
    if (!p || errors.tutar || errors.tarih) return
    create.mutate(
      { employeeId: p.id, amount: Number(tutar).toFixed(2), period: periodKey(tarih), description: `${donemFromDate(tarih)} ${t(`hr.personel.odeme.tur.${tur}`)}` },
      {
        onSuccess: () => {
          toast.success(t('hr.personel.actions.odeme.created', { name: p.ad }))
          onClose()
        },
        onError: (error) => toast.error(apiErrorMessage(error)),
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      icon={<Wallet />}
      title={t('hr.personel.actions.odeme.title')}
      description={t('hr.personel.actions.odeme.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={<ModalFooter onCancel={onClose} />}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="odeme-personel" label={t('hr.personel.actions.odeme.personel')} error={show(errors.personel)} className="sm:col-span-2">
          <Select
            id="odeme-personel"
            size="lg"
            className="w-full rounded-xl"
            value={personelId}
            onChange={setPersonelId}
            placeholder={t('hr.personel.actions.odeme.personel')}
            options={list.map((p) => ({ value: p.id, label: `${p.ad} · ${p.departman}` }))}
          />
        </Field>
        <Field id="odeme-tur" label={t('hr.personel.odemeGecmis.tur')}>
          <Select
            id="odeme-tur"
            size="lg"
            className="w-full rounded-xl"
            value={tur}
            onChange={(v) => setTur(v as OdemeTur)}
            options={TURLER.map((x) => ({ value: x, label: t(`hr.personel.odeme.tur.${x}`) }))}
          />
        </Field>
        <Field id="odeme-tutar" label={t('hr.personel.actions.odeme.tutar')} error={show(errors.tutar)}>
          <NumberInput id="odeme-tutar" className="h-10 rounded-xl" value={tutar} onChange={setTutar} minDecimalsOnBlur={2} suffix="₺" aria-invalid={!!show(errors.tutar)} />
        </Field>
        <Field id="odeme-tarih" label={t('hr.personel.odemeGecmis.tarih')} error={show(errors.tarih)} className="sm:col-span-2">
          <DateField id="odeme-tarih" className="h-10 rounded-xl" value={tarih} onChange={setTarih} aria-invalid={!!show(errors.tarih)} />
        </Field>
      </div>
    </Modal>
  )
}
